import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Undo2, ShieldCheck, ShoppingCart, Zap, Globe, ArrowUpRight } from "lucide-react";

const OUTCOMES = [
  {
    icon: TrendingUp,
    title: "Increase Sales",
    body: "Help customers buy faster with confident decisions.",
  },
  {
    icon: Undo2,
    title: "Reduce Returns",
    body: "Customers know exactly what they're purchasing.",
  },
  {
    icon: ShieldCheck,
    title: "Boost Customer Confidence",
    body: "Interactive visualization builds trust before checkout.",
  },
  {
    icon: ShoppingCart,
    title: "Increase Average Order Value",
    body: "Recommend matching products inside the experience.",
  },
  {
    icon: Zap,
    title: "Faster Sales Process",
    body: "Reduce back-and-forth discussions with instant visuals.",
  },
  {
    icon: Globe,
    title: "Works Everywhere",
    body: "In store, on your website, on mobile, via QR, and with sales teams.",
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const cardIn = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function WhyNeiv() {
  return (
    <section className="bg-neutral-50 py-16 sm:py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="text-sm font-bold uppercase tracking-widest text-primary">Why NEIV</p>
          <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
            Why businesses choose NEIV
          </h2>
          <p className="mt-4 text-lg text-neutral-500">
            Not just features — real business outcomes.
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {OUTCOMES.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                variants={cardIn}
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 24 }}
                className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm shadow-neutral-900/[0.03] transition-shadow duration-300 hover:shadow-lg hover:shadow-neutral-900/[0.06] sm:p-8"
              >
                {/* Left accent bar that fills in on hover */}
                <span
                  className="absolute inset-y-0 left-0 w-[3px] origin-top scale-y-0 bg-primary transition-transform duration-500 ease-out group-hover:scale-y-100"
                  aria-hidden="true"
                />

                {/* Faint oversized index number */}
                <span
                  className="pointer-events-none absolute -right-2 -top-4 select-none text-7xl font-black text-neutral-100 transition-colors duration-500 group-hover:text-primary/10"
                  aria-hidden="true"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>

                <div className="relative">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="mt-4 flex items-start justify-between gap-2">
                    <h3 className="text-lg font-bold text-neutral-900">{item.title}</h3>
                    <ArrowUpRight className="mt-0.5 h-4 w-4 flex-shrink-0 -translate-x-1 translate-y-1 text-primary opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:opacity-100" />
                  </div>
                  <p className="mt-2 leading-relaxed text-neutral-500">{item.body}</p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
